/**
 * The Step 4 insight explaining land the home exemption does not cover.
 *
 * WHY. The property questions on that step now change real numbers — land on a separate title, and
 * land beyond 2 hectares where the Extended Land Use Test fails, are assessable assets (see
 * shared/src/calculation/homeLandRules.ts). A client who answers "yes, and it's worth $1,000,000"
 * has just moved their Age Pension and their care fee substantially, and until this panel existed
 * nothing on the screen said so.
 *
 * WHAT IT SAYS, and what it deliberately does not. It states the treatment and the direction of the
 * effect, in the terms of whichever goals the client actually selected. It does NOT quote a precise
 * pension figure: the taper only bites above the assets threshold, which depends on their whole
 * balance sheet, so a number here would be wrong for anyone already over or well under it. The
 * projections are where exact figures belong.
 */
import { ANNUAL_REDUCTION_PER_1000_ASSETS } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { assessableHomeLandOf, extendedLandUseTestApplies } from '../../../shared/src/calculation/homeLandRules.ts';
import type { FactFind, FactFindGoals } from '../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from './initialFactFind.ts';

export interface HomeLandInsightSection {
    title: string;
    /** Distinguishes this card from the living-arrangement one beside it. */
    icon?: 'land';
    subtitle: string | null;
    paragraphs: string[];
    goalsIntro: string | null;
    goalBlocks: { label: string; summary: string }[];
}

const money = (value: number): string =>
    `$${Math.round(value).toLocaleString('en-AU')}`;

/** What this land does to each goal the client selected. Only the goals they chose are shown. */
function goalImpacts(goals: FactFindGoals): { label: string; summary: string }[] {
    const blocks: { label: string; summary: string }[] = [];

    if (goals?.aged_care) {
        blocks.push({
            label: 'Residential aged care',
            summary:
                'This land is counted at its full value in the aged care means test. The home itself is only ever counted up to the capped amount, but land outside the exemption is not the home, so no cap applies to it. That pushes up the means-tested care contribution.',
        });
    }
    if (goals?.home_care) {
        blocks.push({
            label: 'Support at Home',
            summary:
                'Home care contributions are means tested on the same assets, so this land raises what would be paid towards a package.',
        });
    }
    if (goals?.downsize_home) {
        blocks.push({
            label: 'Downsizing',
            summary:
                'This land is already counted, so selling it does not change the assets test the way selling an exempt home does — the value simply moves from land into cash or investments. What changes is the income it then earns, which is income tested.',
        });
    }
    if (goals?.retirement_village) {
        blocks.push({
            label: 'Retirement village',
            summary:
                'Moving to a village does not make this land exempt. If it is kept, it stays an assessable asset alongside whatever the village entry contribution is assessed as.',
        });
    }
    if (goals?.granny_flat) {
        blocks.push({
            label: 'Granny flat',
            summary:
                'Land kept after moving into a granny flat arrangement stays assessable, and is separate from how the granny flat interest itself is assessed.',
        });
    }

    return blocks;
}

/**
 * Builds the land section, or null when there is nothing to say.
 *
 * Three states, all worth a word:
 *   - land counted        → what it does, per goal
 *   - land exempt (ELUT)  → good news, and the fact that it depends on continuing to qualify
 *   - answered but blank  → a prompt, because a missing value silently counts as zero
 */
export function buildHomeLandInsightSection(factFind: DraftFactFind): HomeLandInsightSection | null {
    const home = factFind?.home || {};
    const declaredExcessLand = home.landSize === true;
    const declaredSeparateTitle = home.separateTitles === true;
    if (!declaredExcessLand && !declaredSeparateTitle) return null;

    const assessment = assessableHomeLandOf(factFind as unknown as FactFind);
    const goals = (factFind.goals || {}) as FactFindGoals;

    const excessValue = Number(home.landSizeValue) || 0;
    const separateValue = Number(home.separateTitlesValue) || 0;

    // Answered yes but not valued yet: say what is missing rather than describing a $0 effect.
    if (assessment.total === 0 && !assessment.extendedLandUseTestApplies) {
        const missing: string[] = [];
        if (declaredExcessLand && excessValue === 0) missing.push('the land beyond 2 hectares');
        if (declaredSeparateTitle && separateValue === 0) missing.push('the land on the second title');
        if (missing.length === 0) return null;
        return {
            title: 'Land outside the home exemption',
            icon: 'land',
            subtitle: 'A value is needed before we can model it',
            paragraphs: [
                `Add a value for ${missing.join(' and ')} above. Until then we treat it as nothing, which will understate the assets test and overstate the Age Pension.`,
            ],
            goalsIntro: null,
            goalBlocks: [],
        };
    }

    const paragraphs: string[] = [];

    if (assessment.extendedLandUseTestApplies) {
        paragraphs.push(
            `Because there has been 20 years or more on this property and the extra land is being put to use, Centrelink's extended land use test applies — so ALL the land around the home is exempt from the assets test, however large it is. That is worth confirming with Centrelink, since it rests on both of those answers continuing to hold.`,
        );
    } else if (excessValue > 0) {
        paragraphs.push(
            `Only the home and the first 2 hectares around it are exempt. The ${money(excessValue)} of land beyond that is counted as an ordinary asset, because the extended land use test needs both 20 years on the property and the land being put to use.`,
        );
    }

    if (separateValue > 0) {
        paragraphs.push(
            `A title without the house on it is not part of the principal home, so the ${money(separateValue)} on the second title is counted as an ordinary asset. There is no exemption for it and no size test — it is treated like any other property they own.`,
        );
    }

    if (assessment.total > 0) {
        const annualPensionEffect = (assessment.total / 1000) * ANNUAL_REDUCTION_PER_1000_ASSETS;
        paragraphs.push(
            `Counting ${money(assessment.total)} of land reduces the Age Pension by up to ${money(annualPensionEffect)} a year — $3 per fortnight for every $1,000 — though only for the part of their assets above the pension threshold. The projections show the actual figure for this file.`,
        );
    }

    return {
        title: 'Land outside the home exemption',
        icon: 'land',
        subtitle: assessment.extendedLandUseTestApplies
            ? 'Exempt under the extended land use test'
            : `${money(assessment.total)} counted as an assessable asset`,
        paragraphs,
        goalsIntro:
            assessment.total > 0 && goalImpacts(goals).length > 0
                ? 'What that means for the goals selected in Step 3:'
                : null,
        goalBlocks: assessment.total > 0 ? goalImpacts(goals) : [],
    };
}

/** Re-exported so the panel can badge the exempt case differently without importing the rule twice. */
export { extendedLandUseTestApplies };
